/**
 * Dev events panel: shows the Mini PRD's instrumentation schema firing live.
 *
 * Zero value to user-testing participants, high value at the Scope planning
 * session - eng sees the exact events and properties the PRD makes
 * must-haves ("instrumentation ships with the feature, not retrofitted").
 * The real build wires the same calls to PostHog.
 */
import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { useCaptureFlow } from '../CaptureFlowContext';

export const EventsPanel = () => {
  const { events, resetAll } = useCaptureFlow();
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ position: 'fixed', right: 12, bottom: 84, zIndex: 1300 }}>
      {open && (
        <Box
          sx={{
            width: 320,
            maxHeight: 360,
            overflowY: 'auto',
            backgroundColor: '#101418',
            color: '#d7e0ea',
            borderRadius: 2,
            padding: 1.5,
            marginBottom: 1,
            fontFamily: 'monospace',
            boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
          }}
        >
          <Stack
            direction='row'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography sx={{ fontSize: 11, color: '#8ab4f8' }}>
              PostHog events (prototype log) — Mini PRD instrumentation schema
            </Typography>
            <Box
              component='button'
              aria-label='Reset prototype'
              onClick={resetAll}
              sx={{
                border: '1px solid #44505c',
                borderRadius: 1,
                background: 'none',
                color: '#e9a0a0',
                fontSize: 10,
                fontFamily: 'monospace',
                padding: '2px 8px',
                cursor: 'pointer',
                flexShrink: 0,
                ml: 1,
              }}
            >
              reset
            </Box>
          </Stack>
          {events.length === 0 ? (
            <Typography sx={{ fontSize: 11, color: '#7a8794' }}>
              No events yet. Capture a document to see them fire.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {events.map((event, i) => (
                <Box key={i} sx={{ borderLeft: '2px solid #8ab4f8', pl: 1 }}>
                  <Typography sx={{ fontSize: 11 }}>
                    {event.name}
                    <Typography
                      component='span'
                      sx={{ fontSize: 10, color: '#7a8794', ml: 1 }}
                    >
                      {event.at}
                    </Typography>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: '#9fb0c0',
                      wordBreak: 'break-all',
                    }}
                  >
                    {JSON.stringify(event.payload)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      )}
      <Box
        component='button'
        aria-label='Toggle events panel'
        onClick={() => setOpen((o) => !o)}
        sx={{
          border: 'none',
          borderRadius: 99,
          padding: '6px 12px',
          backgroundColor: open ? '#101418' : 'rgba(16,20,24,0.75)',
          color: '#d7e0ea',
          fontSize: 11,
          fontFamily: 'monospace',
          cursor: 'pointer',
          float: 'right',
        }}
      >
        {`{ } events · ${events.length}`}
      </Box>
    </Box>
  );
};

export default EventsPanel;
